import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ShieldAlert, ShieldCheck, LockOpen, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const EVENT_STYLES = {
  rate_limit: { label: 'Rate limit', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  login_lockout: { label: 'Login lockout', cls: 'bg-orange-500/15 text-orange-400 border-orange-500/30' },
  account_locked: { label: 'Account locked', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  account_unlocked: { label: 'Unlocked', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
};

const fmtTs = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export const SecuritySection = () => {
  const [data, setData] = useState(null);
  const [unlocking, setUnlocking] = useState(null);

  const load = () => {
    api.getSecurityOverview()
      .then(res => setData(res.data))
      .catch(() => setData({ locked_users: [], events: [], limits: {} }));
  };

  useEffect(() => { load(); }, []);

  const handleUnlock = async (u) => {
    if (!window.confirm(`Unlock ${u.name || u.email}? The account will regain full access immediately.`)) return;
    setUnlocking(u.id);
    try {
      await api.unlockUser(u.id);
      toast.success(`${u.name || u.email} unlocked`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock');
    } finally {
      setUnlocking(null);
    }
  };

  if (!data) return null;
  const limits = data.limits || {};

  return (
    <div className="border border-border rounded-sm bg-card p-4" data-testid="security-section">
      <h3 className="text-sm font-medium text-foreground mb-1 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-red-400" /> Security Monitor
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Anti-scraping protection for non-admin accounts — max {limits.requests_per_minute || '—'} requests/minute;
        {' '}{limits.strikes_to_lock || '—'} breaches or {limits.login_lockouts_to_lock || '—'} login lockouts auto-lock the account until an admin unlocks it. Admins are exempt.
      </p>

      {data.locked_users.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="security-locked-list">
          {data.locked_users.map((u, i) => (
            <div key={u.id} data-testid={`security-locked-${i}`}
              className="flex items-center justify-between gap-3 border border-red-500/40 bg-red-500/5 rounded-sm px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {u.name} <span className="text-[11px] text-muted-foreground font-mono ml-1">{u.email}</span>
                </p>
                <p className="text-[11px] text-red-400 truncate">{u.security_lock_reason} · {fmtTs(u.security_locked_at)}</p>
              </div>
              <Button size="sm" onClick={() => handleUnlock(u)} disabled={unlocking === u.id}
                data-testid={`security-unlock-btn-${i}`}
                className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm shrink-0">
                {unlocking === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><LockOpen className="w-3.5 h-3.5 mr-1" /> Unlock</>}
              </Button>
            </div>
          ))}
        </div>
      )}

      {data.events.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-emerald-400 border border-emerald-500/20 bg-emerald-500/5 rounded-sm px-3 py-2.5" data-testid="security-all-clear">
          <ShieldCheck className="w-4 h-4" /> No security events — all quiet.
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-secondary/50 uppercase tracking-wider text-muted-foreground font-bold text-[10px]">
                <th className="text-left py-2 px-3">Event</th>
                <th className="text-left py-2 px-3">User</th>
                <th className="text-left py-2 px-3">Detail</th>
                <th className="text-left py-2 px-3">IP</th>
                <th className="text-right py-2 px-3">When</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((e, i) => {
                const style = EVENT_STYLES[e.type] || { label: e.type, cls: 'bg-secondary text-muted-foreground border-border' };
                return (
                  <tr key={e.id} data-testid={`security-event-${i}`} className="border-t border-border">
                    <td className="py-2 px-3">
                      <span className={`inline-block text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${style.cls}`}>{style.label}</span>
                    </td>
                    <td className="py-2 px-3 text-foreground whitespace-nowrap">{e.name || e.email || '—'}</td>
                    <td className="py-2 px-3 text-muted-foreground max-w-[320px] truncate" title={`${e.detail} ${e.path || ''}`}>{e.detail}</td>
                    <td className="py-2 px-3 text-muted-foreground font-mono">{e.ip || '—'}</td>
                    <td className="py-2 px-3 text-right text-muted-foreground font-mono whitespace-nowrap">{fmtTs(e.at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
